import { LearningType } from "@tsin-core/entities";

export type AttendanceStatus = "Present" | "Absent" | "AWOL";

/** How a SessionLeader replacement was triggered / Comment un remplacement de leader a été déclenché */
export type LeaderReplacementType = 'AutoWeekAbsent' | 'ManualReassign' | 'SuggestionAccepted';

/** A single scored candidate to replace an absent leader / Candidat classé pour remplacer un leader absent */
export interface ScoredCandidateModel {
  userId: string;
  userName: string;
  firstName: string;
  lastName: string;
  PGYLevel: string;
  applicationId: string;
  score: number;
  /** Human-readable rationale / Justification lisible */
  rationale: string;
  rationaleFr: string;
}

/** One leadership slot with its ranked replacement candidates / Créneau de leadership avec candidats de remplacement */
export interface LeaderSlotSuggestionModel {
  sessionLeaderId: string;
  caseId: string;
  track: string;
  group: string;
  candidates: ScoredCandidateModel[];
}

/** Request to manually reassign a leader slot / Demande de réassignation manuelle d'un créneau de leader */
export interface ReassignLeaderRequest {
  sessionLeaderId: string;
  newUserId: string;
  newApplicationId: string;
  replacementType: LeaderReplacementType;
  comment?: string;
}

/** Request for full-week auto-reassignment / Demande de réassignation automatique pour une semaine entière */
export interface ReassignWeekAbsentRequest {
  weeklyScheduleId: string;
  userId: string;
}

/** Request to transfer a learner from one schedule to another / Demande de transfert d'un apprenant entre horaires */
export interface TransferLearnerRequest {
  applicationId: string;
  fromWeeklyScheduleId: string;
  toWeeklyScheduleId: string;
}

export interface AttendanceStateModel {
  attendances: AttendanceModel[];
  asycUsers: AsyncActivitiesUser[];
  loading: LoadingAttendanceState;
  error: any | null;
  /** Suggestions for the currently-selected session user / Suggestions pour l'utilisateur de session sélectionné */
  leaderSuggestions: LeaderSlotSuggestionModel[];
  weekAbsentResult: { reassigned: string[]; conflicts: string[]; conflictsFr: string[] } | null;
}

export interface AttendanceRequest {
  id: string;
  attendance: AttendanceStatus;
  comment?: string;
}

export interface BulkAttendanceRequest {
  data: AttendanceRequest[];
}

export interface AttendanceScheduleRequest {
  weeklyScheduleId: string;
  sessionId: string;
}

export interface AsynchronousActivityRequest {
  userId: string,
  weeklyScheduleId: string,
  asynchronousActivity: {
    sensitiveClinicalExam: "Complete" | "Not Completed",
    obstetricsInFM: "Complete" | "Not Completed",
    onCallSkillsInFM: "Complete" | "Not Completed",
    palliativeEndOfLifeCare: "Complete" | "Not Completed",
    periodicHealthExam: "Complete" | "Not Completed",
    healthBehaviourChange: "Complete" | "Not Completed"
  }
}

/** Minimal info about a leadership slot the learner holds in the current session */
export interface SessionLeaderRef {
  id: string;                 // SessionLeader._id
  caseId: string;
  track: string;
  group: string;
  /** If this record is a replacement, these audit fields are present */
  replacedUserId?: string;
  replacedUserName?: string;
  replacementType?: LeaderReplacementType;
  replacedAt?: string;
}

export interface AttendanceModel {
  id: string,
  sessionId: string,
  weeklyScheduleId: string,
  learningId: string,
  userId: string,
  userName: string,
  PGYLevel: string | null,
  track: string,
  group: string,
  applicationId: string,
  associatedMedicalSchool: string,
  postgraduateTrainingProgram: string,
  attendance: AttendanceStatus,
  asynchronousActivities: any[],
  comment: string,
  totalPresent: number,
  totalSession: number,
  /** Leadership slots this learner holds for this session / Créneaux de leadership pour cette session */
  leaderIn?: SessionLeaderRef[],
}

export interface AsyncActivitiesUser {
  id: string;
  firstName: string;
  lastName: string;
  PGYLevel: string;
  applicationNo: string;
  postgraduateTrainingProgram: string;
  asynchronousActivities: any[];
  type: LearningType | null;
  weeklyScheduleId?: string;
}

export interface Activity {
  id: string;
  sensitiveClinicalExam: string;
  obstetricsInFM: string;
  onCallSkillsInFM: string;
  palliativeEndOfLifeCare: string;
  periodicHealthExam: string;
  healthBehaviourChange: string;
  userId: string;
  weeklyScheduleId: string;
  // Add any other keys if needed
}

export interface ActivitiesData {
  asynchronousActivities: Activity[];
}

export interface StatusCounts {
  complete: number;
  notCompleted: number;
}

export enum LoadingAttendanceState {
  loadingList = 'loadingList',
  loadingAddUpdate = 'loadingAddUpdate',
  loadingDelete = 'loadingDelete',
  notLoading = 'notLoading',
}
